import React, { useState } from 'react';
import { signInWithPopup, signInWithEmailAndPassword } from 'firebase/auth';
import { auth, googleProvider } from '../services/firebase';
import { ShieldAlert, LogIn, KeyRound } from 'lucide-react';
import { Language } from '../types';

interface ReAuthModalProps {
    userEmail: string;
    authProvider: 'google' | 'email' | 'unknown';
    language: Language;
    onSuccess: () => void;
}

export const ReAuthModal: React.FC<ReAuthModalProps> = ({ userEmail, authProvider, language, onSuccess }) => {
    const [password, setPassword] = useState('');
    const [error, setError] = useState('');
    const [loading, setLoading] = useState(false);

    const handleGoogleReAuth = async () => {
        setLoading(true);
        setError('');
        try {
            await signInWithPopup(auth, googleProvider);
            onSuccess();
        } catch (e: any) {
            if (e.code !== 'auth/popup-closed-by-user') {
                setError(language === 'vietnamese' ? 'Xác thực thất bại. Thử lại.' : 'Authentication failed. Try again.');
            }
        } finally {
            setLoading(false);
        }
    };

    const handleEmailReAuth = async () => {
        if (!password) return;
        setLoading(true);
        setError('');
        try {
            await signInWithEmailAndPassword(auth, userEmail, password);
            onSuccess();
        } catch {
            setError(language === 'vietnamese' ? 'Mật khẩu không đúng. Thử lại.' : 'Incorrect password. Try again.');
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="fixed inset-0 z-[500] flex items-center justify-center bg-black/60 backdrop-blur-sm">
            <div className="bg-white dark:bg-slate-800 rounded-2xl shadow-2xl p-6 w-full max-w-sm mx-4 animate-in zoom-in-95 duration-200">
                <div className="flex flex-col items-center gap-4 text-center">
                    <div className="p-3 bg-red-100 dark:bg-red-900/30 rounded-full">
                        <ShieldAlert size={28} className="text-red-600 dark:text-red-400" />
                    </div>

                    <div>
                        <h2 className="text-lg font-bold text-gray-900 dark:text-gray-100">
                            {language === 'vietnamese' ? 'Phiên đăng nhập hết hạn' : 'Session Expired'}
                        </h2>
                        <p className="text-sm text-gray-500 dark:text-gray-400 mt-2 leading-relaxed">
                            {language === 'vietnamese'
                                ? 'Dữ liệu bạn vừa gõ vẫn an toàn trên thiết bị. Xác thực lại để tiếp tục đồng bộ Cloud mà không mất bất kỳ nội dung nào.'
                                : 'Your unsaved notes are still safe on this device. Re-authenticate to resume Cloud sync without losing any content.'}
                        </p>
                    </div>

                    {authProvider === 'google' && (
                        <button
                            onClick={handleGoogleReAuth}
                            disabled={loading}
                            className="w-full flex items-center justify-center gap-2.5 px-4 py-2.5 bg-white dark:bg-slate-700 border border-gray-300 dark:border-slate-600 text-gray-700 dark:text-gray-200 rounded-lg hover:bg-gray-50 dark:hover:bg-slate-600 font-medium text-sm disabled:opacity-50 shadow-sm transition-colors"
                        >
                            <svg viewBox="0 0 24 24" className="w-4 h-4" xmlns="http://www.w3.org/2000/svg">
                                <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" fill="#4285F4"/>
                                <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/>
                                <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05"/>
                                <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/>
                            </svg>
                            {loading
                                ? (language === 'vietnamese' ? 'Đang xác thực...' : 'Authenticating...')
                                : (language === 'vietnamese' ? 'Tiếp tục với Google' : 'Continue with Google')}
                        </button>
                    )}

                    {authProvider === 'email' && (
                        <div className="w-full space-y-3">
                            <p className="text-xs text-gray-400 font-mono">{userEmail}</p>
                            <div className="relative">
                                <KeyRound size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                                <input
                                    type="password"
                                    placeholder={language === 'vietnamese' ? 'Nhập lại mật khẩu' : 'Re-enter password'}
                                    value={password}
                                    onChange={e => setPassword(e.target.value)}
                                    onKeyDown={e => e.key === 'Enter' && handleEmailReAuth()}
                                    autoFocus
                                    className="w-full pl-9 pr-3 py-2 border border-gray-300 dark:border-slate-600 rounded-lg text-sm bg-white dark:bg-slate-700 text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-indigo-500 outline-none"
                                />
                            </div>
                            <button
                                onClick={handleEmailReAuth}
                                disabled={loading || !password}
                                className="w-full flex items-center justify-center gap-2 px-4 py-2.5 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 font-medium text-sm disabled:opacity-50 transition-colors"
                            >
                                <LogIn size={15} />
                                {loading
                                    ? (language === 'vietnamese' ? 'Đang xác thực...' : 'Authenticating...')
                                    : (language === 'vietnamese' ? 'Xác thực lại' : 'Re-authenticate')}
                            </button>
                        </div>
                    )}

                    {authProvider === 'unknown' && (
                        <p className="text-sm text-gray-500 dark:text-gray-400">
                            {language === 'vietnamese'
                                ? 'Vui lòng tải lại trang và đăng nhập lại.'
                                : 'Please reload the page and log in again.'}
                        </p>
                    )}

                    {error && (
                        <p className="text-xs text-red-600 dark:text-red-400 font-medium">{error}</p>
                    )}
                </div>
            </div>
        </div>
    );
};
